/**
 * AICoachInfoPanel Component
 *
 * Displays AI Coach status, current step description, and assistant presence
 *
 * Responsive:
 * - Mobile: Smaller text and padding
 * - Desktop: Full-size text and spacing
 */

import React from 'react'
import { Box, Typography } from '@mui/material'
import { SparklesIcon } from '@heroicons/react/24/solid'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { STEP_DESCRIPTION_KEYS, AI_COACH_STYLES } from '../constants'
import type { AICoachPart } from '../../../../../../../pure-js/types/AICoach'

interface AICoachInfoPanelProps {
  currentPart: AICoachPart | null
  isAISpeaking: boolean
  isAssistantInCall: boolean
}

export const AICoachInfoPanel: React.FC<AICoachInfoPanelProps> = ({ currentPart, isAISpeaking, isAssistantInCall }) => {
  const { t } = useTranslation()
  // The page already announces completion elsewhere, so this panel shows no "Session completed" text.
  const isCompleted = currentPart === null
  const statusText = isAISpeaking ? t('aiCoach.ui.aiSpeaking') : t('aiCoach.ui.sessionActive')

  return (
    <Box
      sx={{
        pt: { xs: 1.5, sm: 2 },
        borderTop: `1px solid ${INTERAX_COLORS.white10}`
      }}
    >
      {/* Header row with title and status badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 2 },
          mb: 0.5
        }}
      >
        <Typography
          sx={{
            color: INTERAX_COLORS.white,
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 700
          }}
        >
          {t('aiCoach.ui.aiCoach')}
        </Typography>
        {!isCompleted && (
          <Box
            sx={{
              padding: { xs: '2px 6px', sm: '3px 8px' },
              borderRadius: AI_COACH_STYLES.borderRadius.small,
              backgroundColor: isAISpeaking ? `${INTERAX_COLORS.green}22` : `${INTERAX_COLORS.sun}22`,
              border: `1px solid ${isAISpeaking ? INTERAX_COLORS.green + '44' : INTERAX_COLORS.sun + '44'}`
            }}
          >
            <Typography
              sx={{
                color: isAISpeaking ? INTERAX_COLORS.green : INTERAX_COLORS.sun,
                fontSize: { xs: 9, sm: 10 },
                fontWeight: 600
              }}
            >
              {statusText}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Step description */}
      {currentPart !== null && (
        <Typography
          sx={{
            color: INTERAX_COLORS.thunderGrey,
            fontSize: { xs: 11, sm: 12 }
          }}
        >
          {t(STEP_DESCRIPTION_KEYS[currentPart])}
        </Typography>
      )}

      {/* Assistant Presence Indicator */}
      {isAssistantInCall && (
        <Box sx={{ mt: { xs: 1, sm: 1.5 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              padding: { xs: '4px 10px', sm: '6px 12px' },
              borderRadius: AI_COACH_STYLES.borderRadius.pill,
              backgroundColor: `${INTERAX_COLORS.green}33`,
              border: `1px solid ${INTERAX_COLORS.green}44`
            }}
          >
            <SparklesIcon style={{ width: 12, height: 12, color: INTERAX_COLORS.green }} />
            <Typography
              sx={{
                fontSize: { xs: 10, sm: 11 },
                fontWeight: 600,
                color: INTERAX_COLORS.white
              }}
            >
              {t('aiCoach.ui.assistantJoined')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}
